import React, { useState, useEffect } from "react";
import { Alert, Button, Form, Input, InputNumber, Switch } from "antd";
import axios from "../api/axiosInstance";
import { SectionCard, useFlatPage } from "../components/ui";
import notify from "../utils/notify";

/**
 * Email (SMTP) settings.
 * Who may read or change these is decided by the API (role checks), not by anything in this page.
 * There is deliberately no client-side "unlock password": anything compared in the browser can be read
 * from the app bundle.
 */
const AccountSettingsPage = () => {
  useFlatPage();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editSmtp, setEditSmtp] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get("/settings/email");
        form.setFieldsValue(response.data || {});
      } catch (err) {
        console.error("Error fetching email settings:", err);
        setError(err?.response?.data?.message || "Could not load the email settings.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [form]);

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      await axios.post("/settings/email", { ...form.getFieldsValue(true), ...values });
      notify.success("Email settings updated");
    } catch (err) {
      console.error("Error updating email settings:", err);
      notify.error(err?.response?.data?.message || "Failed to update email settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hms-page acct-page">
      {error && <Alert style={{ marginBottom: 12 }} type="error" showIcon title="Email settings unavailable" description={error} />}

      <div className="hms-stack" style={{ maxWidth: 760 }}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={loading}>
          <SectionCard title="Sender addresses">
            <Form.Item
              name="fromEmail"
              label="From email"
              extra="Separate several addresses with commas."
              rules={[{ required: true, message: "Enter a from email" }]}
            >
              <Input placeholder="noreply@yourhotel.com" />
            </Form.Item>
            <Form.Item
              name="adminEmail"
              label="Admin email"
              extra="Separate several addresses with commas."
              rules={[{ required: true, message: "Enter an admin email" }]}
            >
              <Input placeholder="admin@yourhotel.com" />
            </Form.Item>
          </SectionCard>

          <div style={{ height: 16 }} />

          <SectionCard
            title="SMTP server"
            extra={
              <label className="hms-switch-label">
                <Switch checked={editSmtp} onChange={setEditSmtp} />
                Edit SMTP settings
              </label>
            }
          >
            {!editSmtp ? (
              <p className="hms-modal-note" style={{ margin: 0 }}>
                SMTP details are hidden to avoid accidental changes. Turn on "Edit SMTP settings" to change them.
              </p>
            ) : (
              <>
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  title="Wrong SMTP details stop all emails (confirmations, invoices, password resets). Change them only if you are sure."
                />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 16 }}>
                  <Form.Item name="smtpHost" label="SMTP host" rules={[{ required: true, message: "Required" }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="smtpPort" label="Port" rules={[{ required: true, message: "Required" }]}>
                    <InputNumber style={{ width: "100%" }} min={1} max={65535} />
                  </Form.Item>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <Form.Item name="smtpUser" label="SMTP user" rules={[{ required: true, message: "Required" }]}>
                    <Input autoComplete="off" />
                  </Form.Item>
                  <Form.Item name="smtpPass" label="SMTP password" rules={[{ required: true, message: "Required" }]}>
                    <Input.Password autoComplete="new-password" />
                  </Form.Item>
                </div>
                <Form.Item name="enableSsl" label="Use SSL" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </>
            )}
          </SectionCard>

          <div className="hms-form-actions">
            <Button type="primary" htmlType="submit" loading={saving}>
              Save settings
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
